import React, { useCallback, useState } from 'react';
import { Toolbar } from 'features/command-center';
import { DRAWER_KEYS, ENDPOINTS } from 'features/saved-searches/constants';
import { useShowAlert } from 'hooks';
import useDatatable, {
  buildRowActionsRendererDefinition,
  extractObjectFromDataTable,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import DataTable from 'UI/components/organisms/DataTable';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { RowActions } from 'UI/constants/defaults';
import { ListingFilters } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';

import { CreationSavedSearchDrawer } from '../CreationSavedSearchDrawer';

import { ORDER_BY_OPTIONS, PAGE_KEY, SAVED_SEARCH_COLUMNS } from './SavedSearchDrawer.constants';
import { ScopeSelectorContainer, useDrawerStyles } from './SavedSearchDrawer.styled';

const COPIES = strings.shared.savedSearches;

export const SavedSearchDrawer = ({
  activeSearch,
  entityType,
  onApplySearch,
  onDeleteSearch,
  onClose,
  open = true
}) => {
  const classes = useDrawerStyles();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const isSharedSavedSearchesEnabled = checkIfFeatureFlagEnabled(FeatureFlags.SharedSavedSearches);
  const [uiState, setUiState] = useState({
    isDeleteDialogOpen: false,
    isDeleting: false,
    isEditDrawerOpen: false,
    savedSearchId: null,
    searchName: null
  });
  const { showAlert } = useShowAlert();

  const initialPreferences = getColumnPreferences(
    PAGE_KEY,
    0,
    ORDER_BY_OPTIONS,
    SAVED_SEARCH_COLUMNS
  );

  const paramsExtender = useCallback(
    () => ({
      entityType
    }),
    [entityType]
  );

  const {
    columnPreferences,
    count,
    data,
    getData,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersToggle,
    handleKeywordChange,
    handleOneFilterChange,
    handlePageChange,
    handlePerPageChange,
    listState,
    mainOptions
  } = useDatatable({
    apiInstance: SECONDARY_API_CLIENT,
    columnsDefinitions: SAVED_SEARCH_COLUMNS,
    endpoint: ENDPOINTS.savedSearches,
    entityName: COPIES.entityName,
    excludeUserFilters: [ListingFilters.MyTeam, ListingFilters.All],
    initialPreferences,
    key: `${PAGE_KEY}-${entityType}`,
    orderByOptions: ORDER_BY_OPTIONS,
    paramsExtender,
    shouldScrollOnNavigate: true
  });

  const { isLoading, showWholeSkeleton, page, perPage, keyword, userFilter } = listState;
  const { columns, columnOrder, orderBy, direction } = columnPreferences;

  const handleEditSearch = ({ rowData }) => {
    if (!rowData) return;

    const item = extractObjectFromDataTable(SAVED_SEARCH_COLUMNS, ['id'], rowData);

    setUiState(prev => ({
      ...prev,
      isEditDrawerOpen: true,
      savedSearchId: item.id
    }));
  };

  const handleOpenDeleteDialog = ({ rowData }) => {
    if (!rowData) return;

    const item = extractObjectFromDataTable(SAVED_SEARCH_COLUMNS, ['id', 'name'], rowData);

    setUiState(prev => ({
      ...prev,
      isDeleteDialogOpen: true,
      savedSearchId: item.id,
      searchName: item.name
    }));
  };

  const handleDeleteSearch = async () => {
    try {
      if (!uiState.savedSearchId) return;

      setUiState(prev => ({
        ...prev,
        isDeleting: true
      }));

      await SECONDARY_API_CLIENT.delete(`${ENDPOINTS.savedSearches}/${uiState.savedSearchId}`);

      onDeleteSearch && onDeleteSearch({ searchId: uiState.savedSearchId });
      showAlert({
        title: strings.formatString(COPIES.form.alerts.success, { action: 'deleted' }),
        autoHideDuration: 5000
      });

      setUiState(prev => ({
        ...prev,
        isDeleteDialogOpen: false,
        isDeleting: false,
        savedSearchId: null,
        searchName: null
      }));
      await getData();
    } catch (error) {
      setUiState(prev => ({
        ...prev,
        isDeleting: false
      }));

      showAlert({
        title: strings.formatString(COPIES.form.alerts.error, { action: 'deleting' }),
        error
      });
    }
  };

  const handleApplySearch = async ({ rowData }) => {
    if (!rowData || !onApplySearch) return;

    const item = extractObjectFromDataTable(
      SAVED_SEARCH_COLUMNS,
      ['id', 'name', 'search'],
      rowData
    );
    item && onApplySearch({ search: item, drawerToClose: DRAWER_KEYS.list });
  };

  const actionsButton = buildRowActionsRendererDefinition({
    actions: [
      { name: RowActions.ApplySearch, onClick: handleApplySearch },
      { name: RowActions.Edit, onClick: handleEditSearch },
      { name: RowActions.Delete, onClick: handleOpenDeleteDialog }
    ],
    columns: SAVED_SEARCH_COLUMNS
  });

  const finalColumns = getColumnsToRender([...columns, actionsButton]);

  const handleSaveSearchCompleted = async () => {
    handleCloseCreationSavedSearchDrawer();
    await getData();
  };

  const handleCloseCreationSavedSearchDrawer = () => {
    setUiState(prev => ({
      ...prev,
      isEditDrawerOpen: false,
      savedSearchId: null,
      searchName: null
    }));
  };

  return (
    <FPDrawer
      CardHeaderProps={{ title: strings.shared.savedSearches.title }}
      FPDrawerProps={{
        open,
        onClose,
        classes: { paper: classes.drawerLarge }
      }}
      onClose={onClose}
      hasPaddingContent={false}
    >
      {isSharedSavedSearchesEnabled && (
        <ScopeSelectorContainer>
          <AutocompleteSelect
            defaultOptions={mainOptions}
            disableClearable
            name="userFilter"
            onSelect={handleOneFilterChange}
            selectedValue={userFilter || mainOptions[0]}
          />
        </ScopeSelectorContainer>
      )}

      <DataTable
        columnOrder={columnOrder?.length && columnOrder}
        columns={finalColumns}
        components={{ TableToolbar: Toolbar }}
        count={count ?? 0}
        data={data}
        draggableColumns={{
          enabled: true
        }}
        elevation={0}
        filter={false}
        loading={showWholeSkeleton}
        onColumnDisplayClick={handleColumnDisplayChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnSortChange={handleColumnSortChange}
        onPageClick={handlePageChange}
        onPerPageClick={handlePerPageChange}
        onSearchTextChange={handleKeywordChange}
        onToggleFilters={handleFiltersToggle}
        page={page}
        refreshing={isLoading}
        rowsPerPage={perPage}
        searchText={keyword}
        selectableRows="none"
        selectToolbarPlacement="none"
        sortOrder={{ name: orderBy, direction }}
        subtitle={COPIES.emptyState.subtitle}
        title={COPIES.emptyState.title}
      />

      <When condition={!!uiState.isEditDrawerOpen}>
        <CreationSavedSearchDrawer
          activeSearch={activeSearch}
          entityType={entityType}
          onApplySearch={onApplySearch}
          onClose={handleCloseCreationSavedSearchDrawer}
          onSaveCompleted={handleSaveSearchCompleted}
          open={!!uiState.isEditDrawerOpen}
          savedSearchId={uiState.savedSearchId}
        />
      </When>

      <When condition={uiState.isDeleteDialogOpen}>
        <DecisionDialog
          fetching={uiState.isDeleting}
          isOpened
          message={strings.formatString(COPIES.deleteDialog.message, {
            name: uiState.searchName ?? ''
          })}
          onClose={() => setUiState(prev => ({ ...prev, isDeleteDialogOpen: false }))}
          onConfirm={handleDeleteSearch}
          showSeverityIcon
          title={COPIES.deleteDialog.title}
          withButtons="YesNo"
        />
      </When>
    </FPDrawer>
  );
};
